import React, { useState, useEffect } from 'react';
import { Box, Paper, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
    Tabs, Tab, CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import RouterIcon from '@mui/icons-material/Router';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { getLogStreams, getClientList } from '../utils/clientUtils';
import { useTranslation } from 'react-i18next';

function LogsLayout() {
    const { t } = useTranslation();
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        Promise.all([getLogStreams(), getClientList()]).then(([streams, clients]) => {
            const ownedNames = new Set((clients ?? []).map(c => c.name));
            const list = (streams ?? []).filter(id => ownedNames.has(id));
            setDevices(list);
            setLoading(false);
            if (list.length > 0 && !location.pathname.split('/logs/')[1]) {
                navigate(`/logs/${list[0]}`, { replace: true });
            }
        });
    }, [location.pathname, navigate]);

    const activeDevice = location.pathname.split('/logs/')[1] ?? '';
    const activeTabIndex = devices.indexOf(activeDevice);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    const Sidebar = (
        <Box sx={{ width: 200, flexShrink: 0 }} role="navigation" aria-label="device logs submenu">
            <List dense>
                {devices.length === 0 ? (
                    <ListItem>
                        <ListItemText primary={t('logs.noDevices')} primaryTypographyProps={{ color: 'text.secondary', fontSize: '0.875rem' }} />
                    </ListItem>
                ) : devices.map(deviceId => (
                    <ListItem key={deviceId} disablePadding>
                        <ListItemButton
                            component={NavLink}
                            to={`/logs/${deviceId}`}
                            selected={activeDevice === deviceId}
                            sx={{ '&.active': { backgroundColor: 'action.selected' } }}
                        >
                            <ListItemIcon sx={{ minWidth: 32 }}>
                                <RouterIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary={deviceId}
                                primaryTypographyProps={{ fontSize: '0.875rem' }}
                            />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
        </Box>
    );

    if (isMobile) {
        return (
            <Box sx={{ pt: 2 }}>
                <Tabs
                    value={activeTabIndex !== -1 ? activeTabIndex : false}
                    onChange={(_, i) => navigate(`/logs/${devices[i]}`)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        mb: 2,
                        '& .MuiTab-root': { minWidth: 0, px: 2, fontSize: '0.8rem' },
                    }}
                >
                    {devices.map(deviceId => (
                        <Tab key={deviceId} label={deviceId} />
                    ))}
                </Tabs>
                <Paper elevation={2} sx={{ p: 2 }}>
                    <Outlet />
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
                {Sidebar}
                <Box sx={{ flex: 1 }}>
                    <Paper elevation={4} sx={{ p: 2, mt: 2, textAlign: 'left' }}>
                        <Outlet />
                    </Paper>
                </Box>
            </Box>
        </Box>
    );
}

export default LogsLayout;
